/**
 * Authenticated satisFINDER® revision page (Part A4).
 *
 * Reached from the revision email link via `/auth?next=/my-finds/:responseId`.
 * Requires login. Once authenticated, fetches the FIND and renders ALL
 * questions read-only EXCEPT the flagged ones — those get a coral border,
 * an editable input, and the QA note attached.
 *
 * Submission writes ONLY to a `revised_answer` overlay on each flag entry
 * (per the immutability rule); the original `answers` dictionary in Mongo
 * stays pristine.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle, CheckCircle2, Clock, Flag, ShieldCheck, ArrowLeft,
  Star, FileText, MessageSquare, Calendar, Hash,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  cleanQuestionLabel, getQuestionType, getQuestionLabel,
  DISPLAY_ONLY_TYPES, CHOICE_TYPES, NPS_TYPES, NUMBER_TYPES,
  RATING_TYPES, TEXT_TYPES,
} from '@/lib/answerFormatting';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
}

function resolveOptions(q) {
  const qtype = getQuestionType(q);
  if (qtype === 'yes_no') return [{ label: 'Yes' }, { label: 'No' }, { label: 'N/A' }];
  const opts = q.options || q.choices || [];
  return (Array.isArray(opts) ? opts : []).map(o => {
    if (typeof o === 'string') return { label: o };
    if (o && typeof o === 'object') return { label: o.label || o.text || String(o.value ?? '') };
    return null;
  }).filter(Boolean);
}

function selectedLabels(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map(x => (x && typeof x === 'object') ? (x.label || x.text || String(x.value ?? '')) : String(x)).filter(Boolean);
  if (typeof raw === 'object') {
    if (Array.isArray(raw.value)) return raw.value.map(x => (x && typeof x === 'object') ? (x.label || x.text || String(x.value ?? '')) : String(x)).filter(Boolean);
    return [raw.label || (raw.value != null ? String(raw.value) : '')].filter(Boolean);
  }
  return [String(raw)].filter(Boolean);
}

function answerToString(raw) {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number') return String(raw);
  if (Array.isArray(raw)) return selectedLabels(raw).join(', ');
  if (typeof raw === 'object') {
    if (raw.transcription) return raw.transcription;
    if (raw.label) return raw.label;
    if (raw.text) return raw.text;
    if ('value' in raw) return answerToString(raw.value);
  }
  return '';
}

function buildSelectionPayload(qtype, selection) {
  if (qtype === 'yes_no') {
    const v = selection[0];
    if (!v) return null;
    const lower = String(v).toLowerCase();
    const map = {
      yes: { value: 'yes', score_value: 1 },
      no: { value: 'no', score_value: 0 },
      'n/a': { value: 'n/a', score_value: null },
    };
    return { label: v, ...(map[lower] || { value: lower }) };
  }
  if (qtype === 'multiple_choice') {
    return selection.map(label => ({ label }));
  }
  return { label: selection[0] || null };
}

function ReadOnlyAnswer({ q, raw }) {
  const qtype = getQuestionType(q);
  const empty = (
    raw == null ||
    (typeof raw === 'string' && raw.trim() === '') ||
    (Array.isArray(raw) && raw.length === 0) ||
    (typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw).length === 0)
  );

  if (empty) {
    return <p className="text-sm italic text-[#94A3B8]">(no answer)</p>;
  }

  if (qtype === 'voice' || (typeof raw === 'object' && raw && raw.transcription)) {
    return (
      <div className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-[#94A3B8]">Voice transcript</p>
        <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{raw.transcription || '(no transcript)'}</p>
      </div>
    );
  }
  if (RATING_TYPES.has(qtype)) {
    const v = typeof raw === 'object' ? (raw.value ?? raw.score_value) : raw;
    const n = Math.max(0, Math.min(5, Number(v) || 0));
    return (
      <span className="inline-flex items-center gap-0.5">
        {Array.from({ length: 5 }, (_, i) => (
          <Star key={i} className={`w-4 h-4 ${i < n ? 'fill-[#F1D511] text-[#F1D511]' : 'text-[#E2E8F0]'}`} />
        ))}
        <span className="text-xs text-[#64748B] ml-1.5">{n}/5</span>
      </span>
    );
  }
  if (qtype === 'file_upload' && Array.isArray(raw)) {
    return (
      <div className="space-y-1 text-sm text-[#475569]">
        {raw.map((f, i) => (
          <div key={i} className="inline-flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> {f.name || 'file'}
          </div>
        ))}
      </div>
    );
  }
  if (CHOICE_TYPES.has(qtype)) {
    return <p className="text-sm text-[#0F172A]">{selectedLabels(raw).join(', ') || '(no selection)'}</p>;
  }
  return <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{answerToString(raw)}</p>;
}

function EditableAnswer({ q, value, onChange }) {
  const qtype = getQuestionType(q);

  if (TEXT_TYPES.has(qtype)) {
    if (qtype === 'short_text') {
      return (
        <Input
          value={typeof value === 'string' ? value : answerToString(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter your revised answer"
          data-testid={`revise-input-${q.question_id || q.id}`}
        />
      );
    }
    return (
      <Textarea
        rows={4}
        value={typeof value === 'string' ? value : answerToString(value)}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter your revised answer"
        data-testid={`revise-textarea-${q.question_id || q.id}`}
      />
    );
  }

  if (NUMBER_TYPES.has(qtype) || NPS_TYPES.has(qtype)) {
    const num = (typeof value === 'object' && value)
      ? (value.value ?? value.score_value ?? '')
      : (value ?? '');
    return (
      <Input
        type="number"
        value={num === null || num === undefined ? '' : String(num)}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        data-testid={`revise-number-${q.question_id || q.id}`}
      />
    );
  }

  if (RATING_TYPES.has(qtype)) {
    const current = Number(
      (typeof value === 'object' && value)
        ? (value.value ?? value.score_value ?? 0)
        : (value ?? 0)
    );
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            type="button"
            onClick={() => onChange({ value: n, score_value: n })}
            className="p-0.5"
            data-testid={`revise-star-${q.question_id || q.id}-${n}`}
          >
            <Star className={`w-6 h-6 ${n <= current ? 'fill-[#F1D511] text-[#F1D511]' : 'text-[#CBD5E1]'}`} />
          </button>
        ))}
        <span className="text-xs text-[#64748B] ml-2">{current}/5</span>
      </div>
    );
  }

  if (qtype === 'yes_no') {
    const current = (value && typeof value === 'object') ? (value.label || value.value || '') : (value || '');
    return (
      <div className="flex gap-2">
        {['Yes', 'No', 'N/A'].map(lbl => (
          <button
            key={lbl}
            type="button"
            onClick={() => onChange(buildSelectionPayload('yes_no', [lbl]))}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
              String(current).toLowerCase() === lbl.toLowerCase()
                ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]'
                : 'bg-white border-[#CBD5E1] text-[#475569] hover:border-[#1A3E6F]'
            }`}
            data-testid={`revise-yesno-${q.question_id || q.id}-${lbl.toLowerCase()}`}
          >
            {lbl}
          </button>
        ))}
      </div>
    );
  }

  if (qtype === 'single_choice' || qtype === 'button_choice') {
    const options = resolveOptions(q);
    const current = (value && typeof value === 'object') ? (value.label || '') : (value || '');
    return (
      <div className="grid gap-2">
        {options.map((o, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChange(buildSelectionPayload(qtype, [o.label]))}
            className={`text-left px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
              current === o.label
                ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]'
                : 'bg-white border-[#CBD5E1] text-[#475569] hover:border-[#1A3E6F]'
            }`}
            data-testid={`revise-choice-${q.question_id || q.id}-${i}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }

  if (qtype === 'dropdown') {
    const options = resolveOptions(q);
    const current = (value && typeof value === 'object') ? (value.label || '') : (value || '');
    return (
      <Select value={current} onValueChange={(v) => onChange(buildSelectionPayload(qtype, [v]))}>
        <SelectTrigger data-testid={`revise-dropdown-${q.question_id || q.id}`}>
          <SelectValue placeholder="Choose…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o, i) => (
            <SelectItem key={i} value={o.label}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (qtype === 'multiple_choice') {
    const options = resolveOptions(q);
    const currentSet = new Set(selectedLabels(value));
    const toggle = (lbl, checked) => {
      const next = new Set(currentSet);
      if (checked) next.add(lbl); else next.delete(lbl);
      onChange(buildSelectionPayload('multiple_choice', Array.from(next)));
    };
    return (
      <div className="space-y-2">
        {options.map((o, i) => (
          <label key={i} className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={currentSet.has(o.label)}
              onCheckedChange={(c) => toggle(o.label, !!c)}
              data-testid={`revise-mc-${q.question_id || q.id}-${i}`}
            />
            <span className="text-sm text-[#475569]">{o.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (qtype === 'date') {
    return (
      <Input
        type="date"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`revise-date-${q.question_id || q.id}`}
      />
    );
  }
  if (qtype === 'time') {
    return (
      <Input
        type="time"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`revise-time-${q.question_id || q.id}`}
      />
    );
  }

  // Fallback — generic text edit.
  return (
    <Textarea
      rows={3}
      value={typeof value === 'string' ? value : answerToString(value)}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Enter your revised answer"
      data-testid={`revise-fallback-${q.question_id || q.id}`}
    />
  );
}

export default function MyFindRevise() {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [find, setFind] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revisedAnswers, setRevisedAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Bounce to login, return here after.
      const next = encodeURIComponent(`/my-finds/${responseId}`);
      navigate(`/auth?next=${next}`, { replace: true });
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/my-finds/${encodeURIComponent(responseId)}`, { credentials: 'include' });
        const data = await res.json();
        if (!res.ok) {
          setError(data?.detail || 'Could not load this FIND.');
          return;
        }
        setFind(data);
        // Pre-fill the editor state. For text/voice types we start EMPTY so
        // the satisFINDER consciously types her revised answer (and we don't
        // accidentally let her submit the original verbatim). For choice /
        // rating / number types we seed the structured original value so she
        // sees what she selected and can re-pick.
        const initial = {};
        for (const f of (data.active_flags || [])) {
          const qid = f.question_id;
          const q = (data.questions || []).find(qq => (qq.question_id || qq.id) === qid);
          const qtype = q ? getQuestionType(q) : '';
          const cur = (data.answers || {})[qid];
          if (TEXT_TYPES.has(qtype) || qtype === 'voice' || qtype === 'short_text' || qtype === 'long_text' || qtype === 'text') {
            initial[qid] = '';
          } else {
            initial[qid] = cur ?? '';
          }
        }
        setRevisedAnswers(initial);
      } catch {
        setError('Network error — please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, responseId, navigate]);

  const flagMap = useMemo(() => {
    const m = {};
    for (const f of (find?.active_flags || [])) m[f.question_id] = f;
    return m;
  }, [find]);

  const orderedQuestions = useMemo(() => {
    const qs = find?.questions || [];
    return qs.filter(q => !DISPLAY_ONLY_TYPES.has(getQuestionType(q)));
  }, [find]);

  const canSubmit =
    find?.can_edit &&
    Object.keys(flagMap).length > 0 &&
    Object.keys(flagMap).every(qid => {
      const v = revisedAnswers[qid];
      if (v == null) return false;
      if (typeof v === 'string') return v.trim().length > 0;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') return Object.keys(v).length > 0;
      return true;
    });

  const submit = async () => {
    if (!canSubmit) {
      toast.error('Please fill in every flagged question before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/my-finds/${encodeURIComponent(responseId)}/revise`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ revised_answers: revisedAnswers }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || 'Could not submit your revision.');
        return;
      }
      toast.success('Revision submitted. Thank you!');
      setDone(true);
    } catch {
      toast.error('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Loading your FIND…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-[#E2E8F0] p-8 text-center space-y-4" data-testid="my-find-revise-error">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
          <h1 className="font-['Montserrat'] text-xl font-bold text-[#0F172A]">We can&apos;t open this FIND</h1>
          <p className="text-sm text-[#64748B]">{error}</p>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>Back to dashboard</Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-emerald-200 p-8 text-center space-y-4" data-testid="my-find-revise-done">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
          <h1 className="font-['Montserrat'] text-xl font-bold text-[#0F172A]">Thank you!</h1>
          <p className="text-sm text-[#64748B]">
            Your revised FIND has been received and is now back with the satisFIND<sup>®</sup> QA team for review. You&apos;ll hear from us when it&apos;s been published.
          </p>
          <Button onClick={() => navigate('/dashboard')} className="bg-[#1A3E6F] hover:bg-[#15325A] text-white">
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!find) return null;

  const round = find.revision_round || 1;
  const maxRounds = find.revision_max_rounds || 2;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-[#475569] inline-flex items-center gap-1.5 hover:text-[#1A3E6F]"
            data-testid="my-find-revise-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <span className="text-xs text-[#64748B]">Logged in as <span className="font-semibold">{user?.email}</span></span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Banner */}
        <section className="bg-white rounded-xl border border-[#E2E8F0] p-5 space-y-2" data-testid="my-find-revise-banner">
          <div className="flex items-center gap-2 text-[#1A3E6F]">
            <ShieldCheck className="w-5 h-5" />
            <h1 className="font-['Montserrat'] text-xl font-bold">Revise your FIND</h1>
          </div>
          <p className="text-sm text-[#475569]">
            The QA team flagged a few questions on your FIND
            <span className="font-mono mx-1 px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#0F172A]">{find.public_id || find.response_id}</span>
            for clarification. Please review and update <span className="font-semibold">only the flagged answers</span> below.
          </p>
          <div className="flex items-center flex-wrap gap-3 text-xs text-[#64748B] pt-1">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {find.branch_name || '—'}</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Visit: {fmtDate(find.visit_datetime)}</span>
            <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" /> Round {round} of {maxRounds}</span>
            <span className="inline-flex items-center gap-1 text-rose-700 font-semibold">
              <Flag className="w-3 h-3" /> {find.active_flags?.length || 0} flagged
            </span>
          </div>
        </section>

        {/* Not currently revisable */}
        {!find.can_edit && (
          <section className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900" data-testid="my-find-revise-locked">
            <p className="font-semibold mb-1">This FIND is not currently open for revision.</p>
            <p>Status: <span className="font-mono">{find.qa_status || 'unknown'}</span>. If you believe this is a mistake please contact the satisFIND<sup>®</sup> QA team.</p>
          </section>
        )}

        {/* Questions */}
        <section className="space-y-4">
          {orderedQuestions.map((q, idx) => {
            const qid = q.question_id || q.id;
            const flag = flagMap[qid];
            const isEditable = !!flag && find.can_edit;
            const qtext = getQuestionLabel(q) || cleanQuestionLabel(q.question_text || q.label || '');
            const raw = (find.answers || {})[qid];
            return (
              <article
                key={qid || idx}
                className={`bg-white rounded-xl border p-5 space-y-3 ${isEditable ? 'border-2 border-[#FB7185] ring-2 ring-[#FECDD3]' : 'border-[#E2E8F0]'}`}
                data-testid={isEditable ? `revise-flagged-${qid}` : `revise-readonly-${qid}`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-xs font-bold text-[#94A3B8] mt-0.5">{idx + 1}.</span>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-semibold text-[#0F172A]">{qtext}</p>
                    {isEditable && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 uppercase tracking-wider">
                        <Flag className="w-3 h-3" /> Needs your update
                      </span>
                    )}
                  </div>
                </div>

                {isEditable ? (
                  <>
                    {flag?.qa_note && (
                      <div className="bg-amber-50 border-l-4 border-amber-400 px-3 py-2 rounded-r" data-testid={`revise-qa-note-${qid}`}>
                        <p className="text-[11px] uppercase tracking-wider font-bold text-amber-800 mb-0.5 inline-flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> QA note
                        </p>
                        <p className="text-sm text-amber-900 whitespace-pre-wrap">{flag.qa_note}</p>
                      </div>
                    )}
                    {raw != null && (
                      <details className="text-xs text-[#64748B] bg-[#F8FAFC] border border-dashed border-[#E2E8F0] rounded p-2">
                        <summary className="cursor-pointer font-semibold">See your original answer</summary>
                        <div className="mt-2"><ReadOnlyAnswer q={q} raw={raw} /></div>
                      </details>
                    )}
                    <div data-testid={`revise-editor-${qid}`}>
                      <EditableAnswer
                        q={q}
                        value={revisedAnswers[qid]}
                        onChange={(v) => setRevisedAnswers(prev => ({ ...prev, [qid]: v }))}
                      />
                    </div>
                  </>
                ) : (
                  <ReadOnlyAnswer q={q} raw={raw} />
                )}
              </article>
            );
          })}
        </section>

        {/* Submit bar */}
        {find.can_edit && (
          <div className="sticky bottom-0 -mx-4 px-4 py-4 bg-white border-t border-[#E2E8F0] flex items-center justify-between gap-3" data-testid="my-find-revise-submit-bar">
            <p className="text-xs text-[#64748B]">
              Only the flagged questions can be edited. Your original answers will remain in the audit trail.
            </p>
            <Button
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
              data-testid="my-find-revise-submit"
            >
              {submitting ? 'Submitting…' : 'Submit revised FIND'}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
